import { ProductQuickView } from '../components/product-quick-view/ProductQuickView';
import { CLASS_ACTIVE, CLASS_WISHLIST_INITIALISED, CLASS_HIDDEN } from '../tools/constants/classes';
import { t } from '@process-creative/slate-theme-tools';

const CLASS_EMPTY =      'is-empty';

const SELECTOR_WISHLIST_BUTTON =    '[data-wishlist-button]';
const SELECTOR_WISHLIST =           '[data-wishlist]';
const SELECTOR_WISHLIST_CONTAINER = '[data-wishlist-container]';
const SELECTOR_WISHLIST_ICON =      '[data-wishlist-close-icon]';
const SELECTOR_WISHLIST_COUNT =     '[data-wishlist-count]';

const wishlistSection = document.querySelector(SELECTOR_WISHLIST);
const wishlistContainer = document.querySelector(SELECTOR_WISHLIST_CONTAINER);
const wishlistCounts = document.querySelectorAll(SELECTOR_WISHLIST_COUNT);

const WISHLIST_ICON = wishlistContainer?.querySelector(SELECTOR_WISHLIST_ICON)?.innerHTML;

let myWishlist;
let myWishlistContents;

// Price formatting function
const formatPrice = (price: number): string => {
  const win = window as Window & typeof globalThis & { Currency: { currency: string, format: string } };
  const currency = win.Currency?.currency || 'AUD';
  const format = win.Currency?.format || '${{amount}}';
  
  // Convert cents to dollars and format
  const amount = (price / 100).toFixed(2);
  return format.replace('{{amount}}', amount);
};

export const rebindWishlist = () => {
  // Init callbacks object
  // @ts-ignore
  if (!window.SwymCallbacks) {
    // @ts-ignore
    window.SwymCallbacks = [];
  }
  
  // Add rebind to stack of callbacks
  // @ts-ignore
  window.SwymCallbacks.push(rebindWishlistCallback);
}

const updateWishlistIDThenRebind = (swat) => {
  const listConfig = {
    "lname": "wishlist",
  };

  const onSuccess = (newList) => {
    myWishlist = newList;
    rebindWishlistCallback(swat);
  }

  const onError = (error) => {
    console.log("Error while creating a List", error);
  }

  swat.createList(listConfig, onSuccess, onError);
}

const updateWishlistContentsThenRebind = (swat) => {
  if(!myWishlist) return;

  const listConfig = {
    "lid": myWishlist.lid,
  };

  // Define success callback
  const onSuccess = function(listContents) {
    myWishlistContents = listContents;
    rebindWishlistCallback(swat);
  }

  // Define error callback
  const onError = function(error) {
    myWishlistContents = [];
    rebindWishlistCallback(swat);
  }

  swat.fetchListCtx(listConfig, onSuccess, onError);
}

const rebindWishlistCallback = (swat) => {
  if (swat) {
    // check if we know the wishlist ID. If we don't, get that first (will re-call this function)
    if(!myWishlist) {
      updateWishlistIDThenRebind(swat);
      return;
    }
    
    // same idea but for the contents of the list
    if(!myWishlistContents) {
      updateWishlistContentsThenRebind(swat);
      return;
    }

    // Use async/await to handle the async rerenderWishlists
    (async () => {
      await rerenderWishlists();
      
      // find and bind unbound wishlist buttons
    const wishlistButtons = document.querySelectorAll(SELECTOR_WISHLIST_BUTTON) as NodeListOf<HTMLButtonElement>;

    wishlistButtons.forEach(button => {
      const variantID = button.dataset.variant;
      const productID = button.dataset.product;
      const productURL = button.dataset.url;
      
      if(!variantID || !productID || !productURL) return;
      
      // If already in list make active
      const matchedProduct = myWishlistContents.find(item => item.epi == variantID && item.empi == productID);
      if(matchedProduct) {
        button.classList.add(CLASS_ACTIVE);
      } else {
        button.classList.remove(CLASS_ACTIVE);
      }

      if(!button.classList.contains(CLASS_WISHLIST_INITIALISED)) {
        button.classList.add(CLASS_WISHLIST_INITIALISED);

        button.addEventListener('click', e => {
          onWishlistButtonClick(button, swat);
        });
      }
    });
    })();
  }
}

const onWishlistButtonClick = (button, swat) => {
  // If logged out go to account page instead (save current page)
  const variantID = button.dataset.variant;
  const productID = button.dataset.product;
  const productURL = button.dataset.url;
  if(!variantID || !productID || !productURL) return;

  let isRemoving = false;
  // Safety check: ensure myWishlistContents is initialized
  if (myWishlistContents && Array.isArray(myWishlistContents)) {
    const matchedProduct = myWishlistContents.find(item => item.epi == variantID && item.empi == productID);
    if(matchedProduct) isRemoving = true;
  } else {
    console.log('myWishlistContents not initialized yet');
  }


  const productData = {
    epi: variantID.toString(),
    empi: productID.toString(),
    du: `${window.location.origin}${productURL}`
  }


  const onSuccess = (listItem) => {
    updateWishlistContentsThenRebind(swat);
  };

  const onError = (error) => {
    if(isRemoving) {
      console.log("Error while removing a product from list", error);
    } else {
      console.log("Error while adding a product to list", error);
    }
  };

  if(isRemoving) {
    swat.deleteFromList(myWishlist.lid, productData, onSuccess, onError);
  } else {
    swat.addToList(myWishlist.lid, productData, onSuccess, onError);
  }
}

const rerenderWishlists = async () => {
  if(!myWishlistContents) return;

  // set wishlistCount - relevant on all pages
  wishlistCounts?.forEach(wishlistCount => {
    if(myWishlistContents.length > 0) {
      wishlistCount.innerHTML = myWishlistContents.length;
      wishlistCount.classList.add(CLASS_ACTIVE);
    } else {
      wishlistCount.innerHTML = '';
      wishlistCount.classList.remove(CLASS_ACTIVE);
    }
  })


  // code after this point only relevant for wishlist page
  if(!wishlistContainer || !wishlistSection) return
  
  
  if(myWishlistContents.length > 0) {
    wishlistSection.classList.remove(CLASS_EMPTY);
    wishlistContainer.classList.remove(CLASS_HIDDEN);
  } else {
    wishlistSection.classList.add(CLASS_EMPTY);
    wishlistContainer.classList.add(CLASS_HIDDEN);
  }

  let items = '';

  // Process products sequentially to handle async fetching
  for (const product of myWishlistContents) {
    const productUrl = product['du'].replace(window.location.origin, '');
    const productHandle = product['du'].split('products/')[1];
    
    let colorInfo = '';
    let priceInfo = '';
    let compareAtPriceInfo = '';
    let uspTagsInfo = '';
    let badgeInfo = '';

    if (productHandle) {
      try {
        // created product.ajax.liquid to get metafields from product
        const response = await fetch(`/products/${productHandle}?view=ajax`);
        if (response.ok) {
          const fetchedProduct = await response.json();
          const product_colour = fetchedProduct?.product_colour;
          const price = fetchedProduct?.price;
          const compareAtPriceMin = fetchedProduct?.compareAtPriceMin;
          const uspTags = fetchedProduct?.usp_tags;
          const badge = fetchedProduct?.tags;

          if (product_colour) {
            colorInfo = `
              <span class="o-product-thumbnail__colour o-paragraph--2">
                ${product_colour}
              </span>
              <span class="o-product-thumbnail__dot-divider"></span>
            `;
          }

          if (compareAtPriceMin) {
            compareAtPriceInfo = `
              <span class="o-pricing__money o-pricing__compare o-product-thumbnail__pricing-money o-product-thumbnail__pricing-compare">
                ${formatPrice(compareAtPriceMin)}
              </span>
            `;
          }

          if (price) {
            priceInfo = `
              <span class="o-pricing__money o-pricing__price o-product-thumbnail__pricing-money o-product-thumbnail__pricing-price money ${compareAtPriceMin ? 'has-compare' : ''}">
                ${formatPrice(price)}
              </span>
            `;
          }

          if (uspTags && Array.isArray(uspTags)) {
            uspTagsInfo = `
              <ul class="o-product-thumbnail__usp-list">
                ${uspTags.map(tag => `
                  <li class="o-product-thumbnail__usp-item o-paragraph--3" data-usp="${tag.value}">
                    <span class="o-product-thumbnail__usp-badge">${tag.value}</span>
                  </li>
                `).join('')}
              </ul>
            `;
          }

          if (badge && Array.isArray(badge)) {
            const badgeTags = badge.filter(tag => tag.startsWith('badge:')).map(tag => tag.replace('badge:', ''));
            if (badgeTags.length > 0) {
              badgeInfo = badgeTags.map(tag => `
                <div class="o-btn o-product-thumbnail__badge is-badge" aria-label="${tag}">
                  <span>${tag}</span>
                </div>
              `).join('');
            }
          }
        }
      } catch (error) {
        console.log('Error fetching product data:', error);
      }
    }
    
    items += `
      <li
        class="o-product-thumbnail"
        data-product-thumbnail
        data-product-id="${product['empi']}"
        data-variant-id="${product['epi']}"
        data-product-handle="${productHandle || ''}"
      >
        <div class="o-product-thumbnail__inner">
          <div class="o-product-thumbnail__top-wrapper">
            ${badgeInfo}
            <button type="button" class="o-btn is-tertiary o-product-thumbnail__quick-view-btn" data-product-quick-view-btn>
              <span class="o-product-thumbnail__quick-view-btn-text o-paragraph--2">
                ${t('products.product.quick_view_label')}
              </span>
              <svg class="o-product-thumbnail__quick-view-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 5V19M5 12H19" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
            
            <a href="${productUrl}" class="o-product-thumbnail__media-wrapper">
              <span class="o-product-thumbnail__image-wrap is-active">
                <img src="${product['iu']}" class="o-product-thumbnail__image o-media is-primary" alt="${product['dt']}" loading="lazy">
              </span>
            </a>
          </div>
        
          <div class="o-product-thumbnail__content">
            <a href="${productUrl}" class="o-product-thumbnail__title o-heading--6">
              ${product['dt']}
            </a>
            
            <div class="o-product-thumbnail__price-and-colour">
              ${colorInfo}
              <p class="o-pricing o-product-thumbnail__pricing ${compareAtPriceInfo ? 'has-compare' : ''}">
                ${compareAtPriceInfo}
                ${priceInfo}
              </p>
            </div>

            <p class="o-product-thumbnail__usp">
              ${uspTagsInfo}
            </p>
            
            <button 
              class="o-product-thumbnail__wishlist-btn c-wishlist__wishlist-button"
              data-wishlist-button
              data-product="${product['empi']}"
              data-variant="${product['epi']}"
              data-url="${productUrl}"
              type="button"
            >
              <span class="u-visually-hidden">
                ${t('products.product.wishlist_label') || 'Wishlist'}
              </span>
              ${WISHLIST_ICON || ''}
            </button>
          </div>
        </div>
      </li>
    `
  }

  if(items.length > 0) {
    wishlistContainer.innerHTML = `
      <ul class="c-wishlist__list">
        ${items}
      </ul>
    `
  } else {
    wishlistContainer.innerHTML = ``;
  }

  const win = window as Window & { __productQuickViewInstance?: ProductQuickView };
  if (!win.__productQuickViewInstance) {
    win.__productQuickViewInstance = new ProductQuickView();
  }
}

export { onWishlistButtonClick }